import { useEffect, useMemo, useRef } from "react";
import { CookiesProvider } from "react-cookie";
import { loadableReady } from "@loadable/component";
import { hydrateRoot } from "react-dom/client";
import { ApolloClient } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { createBrowserRouter, RouterProvider } from "react-router";
import { AppConfig, AppConfigProvider, useIsMounted } from "@ct-react/core";
import { defineLocale, LocaleProvider, useLocaleContext } from "@ct-react/locale";
import buildApolloClient from "@shared/graphql";
import { allLocales, handleLocaleChange } from "@shared/locale";
import { APOLLO_CACHE_SCRIPT_ID, APP_CMS_ROUTES_SCRIPT_ID, APP_CTX_SCRIPT_ID } from "@shared/constants";
import { RoutingTreeProvider } from "./app/contexts/routing-tree";
import { buildMenuTree, mergedRoutes } from "./app-routing";
import { withElements } from "./app/routes";

const loadingLocale = defineLocale(navigator.languages || [], window.location.pathname, allLocales);

const Wrapper = () => {

  const isMounted = useIsMounted();
  const { locale } = useLocaleContext();

  const config = useRef<AppConfig>(window.__APP_CTX__);

  const routing = useMemo(() => {
    const { router, menuable } = mergedRoutes(window.__APP_DYNAMIC_ROUTES__);
    return {
      router: createBrowserRouter(withElements(router)),
      menu: buildMenuTree(withElements(menuable)),
    };
  }, []);

  const apollo = useMemo<ApolloClient>(() => {
    return buildApolloClient(config.current, false, locale, window.__APOLLO_STATE__);
  }, [ locale ]);

  useEffect(() => {
    if (isMounted || !config) return;
    const scriptTag = document.getElementById(APP_CTX_SCRIPT_ID);
    !!scriptTag && scriptTag.parentNode?.removeChild(scriptTag);
  }, [ config ]);

  useEffect(() => {
    if (isMounted || !routing) return;
    const scriptTag = document.getElementById(APP_CMS_ROUTES_SCRIPT_ID);
    !!scriptTag && scriptTag.parentNode?.removeChild(scriptTag);
  }, [ routing ]);

  useEffect(() => {
    if (isMounted || !apollo) return;
    const scriptTag = document.getElementById(APOLLO_CACHE_SCRIPT_ID);
    !!scriptTag && scriptTag.parentNode?.removeChild(scriptTag);
  }, [ apollo ]);

  return (
    <CookiesProvider>
      <AppConfigProvider value={config.current}>
        <ApolloProvider client={apollo}>
          <RoutingTreeProvider menu={routing.menu}>
            <RouterProvider router={routing.router} />
          </RoutingTreeProvider>
        </ApolloProvider>
      </AppConfigProvider>
    </CookiesProvider>);

}

loadableReady()
  .then(() => handleLocaleChange(loadingLocale))
  .then(() => hydrateRoot(
    document.getElementById("app")!,
    (<LocaleProvider locale={loadingLocale}><Wrapper /></LocaleProvider>)));
